import { SubmissionError } from "redux-form";

import { REPORT_REASONS } from "@js/apps/common/constants";
import type { ReportPayload, ReportType } from "@js/apps/common/forms/report";
import {
  useReportMessageMutation,
  useReportUserMutation,
} from "@js/apps/messenger/api";
import { Snackbar } from "@js/components/snackbar";

import { handleOpenReportModal } from "../../forms/report";

type CommonHandlerPayload = {
  id: number;
  room?: number;
};

type ReportableEntityType = Extract<ReportType, "user" | "message">;

type HandleReportUserPayload = CommonHandlerPayload & {
  type: ReportableEntityType;
  name: string;
};

type ErrorType = { data: Record<string, string> };

export const useReport = () => {
  const [reportUser] = useReportUserMutation();
  const [reportMessage] = useReportMessageMutation();

  const reportErrorHandler = (error: ErrorType) => {
    throw new SubmissionError(error.data);
  };

  const reportUserHandler =
    ({ id }: CommonHandlerPayload) =>
    (data: ReportPayload): Promise<void> => {
      return reportUser({ id, ...data })
        .unwrap()
        .then(() => Snackbar.success("User reported"))
        .catch(reportErrorHandler);
    };

  const reportMessageHandler =
    ({ id, room }: CommonHandlerPayload) =>
    (data: ReportPayload): Promise<void> => {
      return reportMessage({ id, room, ...data })
        .unwrap()
        .then(() => Snackbar.success("Message reported"))
        .catch(reportErrorHandler);
    };

  const handleReportUser =
    ({ type, id, name }: HandleReportUserPayload) =>
    () =>
      handleOpenReportModal({
        name,
        type,
        onSubmit: reportUserHandler({ id }),
        reasons: REPORT_REASONS,
      });

  const handleReportMessage =
    ({ type, id, room, name }: HandleReportUserPayload) =>
    () =>
      handleOpenReportModal({
        type,
        onSubmit: reportMessageHandler({ id, room }),
        reasons: REPORT_REASONS,
        name,
      });

  return {
    handleReportUser,
    handleReportMessage,
  };
};
